import { Navigate, type RouteObject } from 'react-router-dom';
import { LandingPage } from '@/features/landing/LandingPage';
import { HowItWorks } from '@/features/marketing/HowItWorks';
import { Privacy } from '@/features/marketing/Privacy';
import { SignInPage } from '@/features/auth/SignInPage';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { AppShell } from '@/features/shell/AppShell';
import { CreateWeddingPage } from '@/features/onboarding/CreateWeddingPage';
import { HomeTab } from '@/features/tabs/HomeTab';
import { GuestsTab } from '@/features/guests/GuestsTab';
import { VenuesTab } from '@/features/venues/VenuesTab';
import { VendorsTab } from '@/features/vendors/VendorsTab';
import { BudgetTab } from '@/features/budget/BudgetTab';
import { TimelineTab } from '@/features/timeline/TimelineTab';
import { SiteTab } from '@/features/site/SiteTab';
import { SeatingTab } from '@/features/seating/SeatingTab';
import { PublicSitePage } from '@/features/site/PublicSitePage';
import { UnsubscribePage } from '@/features/account/UnsubscribePage';
import { RsvpPage } from '@/features/site/RsvpPage';

/** Deep links are `/app/w/{wedId}/{tab}` so a tab is always shareable. */
export const routes: RouteObject[] = [
  { path: '/', element: <LandingPage /> },
  { path: '/signin', element: <SignInPage /> },
  /*
   * Public marketing pages. Each one also has a pre-rendered HTML shell with its
   * own title and description — see `tools/prerender.mjs` — because no crawler
   * runs JavaScript and an SPA would give all four the same one.
   */
  { path: '/how-it-works', element: <HowItWorks /> },
  { path: '/privacy', element: <Privacy /> },
  /*
   * Public, and outside RequireAuth on purpose: a guest opening their invitation
   * has no account and must never be bounced to a sign-in page. `/w/` is the
   * couple's page, `/r/` is one household's reply — short because both get
   * written on paper.
   */
  { path: '/w/:slug', element: <PublicSitePage /> },
  { path: '/r/:token', element: <RsvpPage /> },
  { path: '/unsubscribe', element: <UnsubscribePage /> },
  {
    path: '/app',
    element: (
      <RequireAuth>
        <Navigate to="/app/w" replace />
      </RequireAuth>
    ),
  },
  {
    path: '/app/new',
    element: (
      <RequireAuth>
        <CreateWeddingPage />
      </RequireAuth>
    ),
  },
  {
    path: '/app/w/:wedId?',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeTab /> },
      { path: 'home', element: <HomeTab /> },
      { path: 'guests', element: <GuestsTab /> },
      { path: 'venues', element: <VenuesTab /> },
      { path: 'vendors', element: <VendorsTab /> },
      { path: 'budget', element: <BudgetTab /> },
      { path: 'seating', element: <SeatingTab /> },
      { path: 'timeline', element: <TimelineTab /> },
      { path: 'site', element: <SiteTab /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];
